/**
 * Presence feature telemetry: subscription volume by Teams surface, status distribution,
 * first-update latency, and stack-ready signal. Uses Webex submitClientMetrics pipeline.
 *
 * **Local verification (important in Microsoft Teams):** the app runs in an **iframe**. Storage and
 * `sessionStorage` set in the **top** DevTools context do **not** apply to the iframe. Either:
 * - Open DevTools → **Console** → context dropdown → select the **iframe** for your app (e.g. localhost),
 *   then `sessionStorage.setItem('DEBUG_PRESENCE_METRICS','1'); location.reload();`
 * - Or append **`?debugPresenceMetrics=1`** to your **app** URL (the iframe src), then reload.
 * - Or in the **iframe** console: `window.__enablePresenceMetricsVerify()` then reload.
 * Filter logs for `[presenceMetrics][verify]`.
 */

import { IUserData } from '../../../adapter-interfaces/src/IMetricsAdapter';
import { getMSTeamsContext } from '../../../logger/src/WebexContext';
import { Logger } from '../../../logger/src/Logger';
import { submitMetrics } from '../services/WebexMetricsService';
import {
  PRESENCE_FIRST_UPDATE_LATENCY_METRIC,
  PRESENCE_STACK_INITIALIZED_METRIC,
  PRESENCE_STATUS_DISTRIBUTION_METRIC,
  PRESENCE_SUBSCRIPTION_REQUEST_METRIC,
} from './MetricUtils';
import { convertPresenceType, PresenceData } from './presenceUtils';

const STATUS_FLUSH_MS = 60_000;

/** Teams host classification for presence "request" segmentation (Jira / business metrics). */
export type PresenceSurface = 'calling_app' | 'chat' | 'channel' | 'unknown';

let statusBuckets: Map<string, number> = new Map();
/** Browser `setInterval` id (`number`); avoid Node `Timer` typing from global `setInterval`. */
let statusFlushTimer: number | null = null;

function isDebugPresenceMetricsUrl(): boolean {
  try {
    const v = new URLSearchParams(window.location.search).get('debugPresenceMetrics');
    return v === '1' || v === 'true' || v === 'yes';
  } catch {
    return false;
  }
}

function isPresenceMetricsVerifyEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const w = window as unknown as { __PRESENCE_METRICS_VERIFY__?: boolean };
    if (w.__PRESENCE_METRICS_VERIFY__ === true) {
      return true;
    }
    if (sessionStorage.getItem('DEBUG_PRESENCE_METRICS') === '1') {
      return true;
    }
    return isDebugPresenceMetricsUrl();
  } catch {
    return false;
  }
}

function submitPresenceMetric(payload: IUserData, context: string): void {
  if (isPresenceMetricsVerifyEnabled()) {
    Logger.info(
      `[presenceMetrics][verify] ${context} → metricEvent=${payload.metricEvent} key=${payload.key ?? ''} value=${payload.value ?? ''} executionTime=${payload.executionTime ?? ''}`
    );
  }
  void submitMetrics(payload).catch((e) => {
    Logger.info(
      `[presenceMetrics] ${context} submit failed: ${e instanceof Error ? e.message : String(e)}`
    );
  });
}

function flushStatusBuckets(): void {
  if (statusBuckets.size === 0) {
    return;
  }
  const snapshot = statusBuckets;
  statusBuckets = new Map();
  snapshot.forEach((count, bucket) => {
    if (count > 0) {
      submitPresenceMetric(
        {
          metricEvent: PRESENCE_STATUS_DISTRIBUTION_METRIC,
          key: bucket,
          value: String(count),
        },
        'statusDistribution(flush)'
      );
    }
  });
}

function ensureStatusFlushTimer(): void {
  if (statusFlushTimer !== null || typeof window === 'undefined') {
    return;
  }
  statusFlushTimer = window.setInterval(() => {
    flushStatusBuckets();
  }, STATUS_FLUSH_MS);
}

/**
 * Resolve where the Teams app is hosted: personal Calling shell vs chat vs channel tab.
 */
export async function resolvePresenceSurface(): Promise<PresenceSurface> {
  try {
    if (typeof window === 'undefined') {
      return 'unknown';
    }
    const ctx = await getMSTeamsContext();
    if (ctx.channel?.id && ctx.team) {
      return 'channel';
    }
    if (ctx.chat?.id) {
      return 'chat';
    }
    return 'calling_app';
  } catch (e) {
    Logger.info(
      `[presenceMetrics] resolvePresenceSurface failed: ${e instanceof Error ? e.message : String(e)}`
    );
    return 'unknown';
  }
}

/** New canonical user subscriptions per registerFeature batch (count only, no user ids). */
export function emitPresenceSubscriptionRequest(featureId: string, newUserCount: number): void {
  if (newUserCount <= 0) {
    return;
  }
  void resolvePresenceSurface().then((surface) => {
    submitPresenceMetric(
      {
        metricEvent: PRESENCE_SUBSCRIPTION_REQUEST_METRIC,
        key: `${surface}|${featureId}`,
        value: String(newUserCount),
      },
      'subscriptionRequest'
    );
  });
}

/** First realtime/list result for a user after ref-count first went to 1 (milliseconds). */
export function emitPresenceFirstUpdateLatency(surface: PresenceSurface, latencyMs: number): void {
  if (latencyMs < 0 || !Number.isFinite(latencyMs)) {
    return;
  }
  submitPresenceMetric(
    {
      metricEvent: PRESENCE_FIRST_UPDATE_LATENCY_METRIC,
      key: surface,
      executionTime: String(Math.round(latencyMs)),
    },
    'firstUpdateLatency'
  );
}

/** Normalize status change into display bucket for distribution metrics (batched per minute). */
export function recordPresenceStatusUpdate(data: PresenceData): void {
  try {
    const { presenceLabel } = convertPresenceType(data.status, data.category);
    const safe = (presenceLabel || 'unknown').replace(/\s+/g, '_').slice(0, 64);
    const prev = statusBuckets.get(safe) || 0;
    statusBuckets.set(safe, prev + 1);
    ensureStatusFlushTimer();
  } catch (e) {
    Logger.info(
      `[presenceMetrics] recordPresenceStatusUpdate skipped: ${e instanceof Error ? e.message : String(e)}`
    );
  }
}

/** PresenceManager + worker ready; once per successful init (session-level usage signal). */
export function emitPresenceStackInitialized(): void {
  void resolvePresenceSurface().then((surface) => {
    submitPresenceMetric(
      {
        metricEvent: PRESENCE_STACK_INITIALIZED_METRIC,
        key: surface,
        value: '1',
      },
      'stackInitialized'
    );
  });
}

/** Stop periodic flush and send pending status counts (call from PresenceManager.destroy). */
export function teardownPresenceMetrics(): void {
  if (statusFlushTimer !== null && typeof window !== 'undefined') {
    window.clearInterval(statusFlushTimer);
    statusFlushTimer = null;
  }
  flushStatusBuckets();
}

/** Call from the iframe console, then reload — enables verify flags in the same storage as the app. */
export function enablePresenceMetricsVerify(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem('DEBUG_PRESENCE_METRICS', '1');
    (window as unknown as { __PRESENCE_METRICS_VERIFY__?: boolean }).__PRESENCE_METRICS_VERIFY__ = true;
    Logger.info(
      '[presenceMetrics] Verify mode enabled in this frame — reload the page, then filter for [presenceMetrics][verify]'
    );
  } catch (e) {
    Logger.info(
      `[presenceMetrics] enablePresenceMetricsVerify failed: ${e instanceof Error ? e.message : String(e)}`
    );
  }
}

if (typeof window !== 'undefined') {
  (window as unknown as { __enablePresenceMetricsVerify?: () => void }).__enablePresenceMetricsVerify =
    enablePresenceMetricsVerify;
}
